import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Stack,
  Typography,
} from "@financeable-com-au/financeable-ui";
import { AuthCard } from "@components/AuthCard";
import { authV2Selector, authV2InvitationName, AUTH_REQUEST_KEYS } from "@/store/slices/authV2Slice";
import type { AppDispatch } from "@/store/store";
import { openSnackbar } from "@/store/slices/snackbarSlice";

const AuthInviteSetName = () => {
  const dispatch = useDispatch<AppDispatch>();
  const authV2 = useSelector(authV2Selector);
  const invitationStatus = authV2?.invitationStatus;
  const loading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.invitationName]);
  const error = authV2?.errors?.[AUTH_REQUEST_KEYS.invitationName];

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  // If error, display error snackbar.
  useEffect(() => {
    if (error?.message) {
      dispatch(
        openSnackbar({
          message: error.message,
          intent: "danger",
        }),
      );
    }
  }, [error, dispatch]);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
    },
    validationSchema: yup.object({
      firstName: yup.string().trim().required("First name is required."),
      lastName: yup.string().trim().required("Last name is required."),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      const payload = {
        ...values,
        token,
      };
      dispatch(authV2InvitationName(payload));
    },
  });
  return (
    <AuthCard onSubmit={() => formik.handleSubmit()}>
      <Stack rowGap={6}>
        <Stack rowGap={3}>
          <Stack flexDirection="column">
            <Typography level="body-lg">Organisation</Typography>
            <Typography level="body-lg" bold>
              {invitationStatus?.invitation?.organisationName}
            </Typography>
          </Stack>
          <Stack flexDirection="column">
            <Typography level="body-lg">Email</Typography>
            <Typography level="body-lg" bold>
              {invitationStatus?.invitation?.email}
            </Typography>
          </Stack>
          <FormControl error={!!formik.errors.firstName}>
            <FormLabel size="md">First name</FormLabel>
            <Input
              placeholder="Your first name"
              type="text"
              value={formik.values.firstName}
              onChange={(event) => {
                formik.setFieldValue("firstName", event.target.value);
                if (formik.errors.firstName) {
                  formik.setFieldError("firstName", undefined);
                }
              }}
              size="lg"
            />
            {formik.errors.firstName ? (
              <FormHelperText>
                <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                {formik.errors.firstName}
              </FormHelperText>
            ) : null}
          </FormControl>
          <FormControl error={!!formik.errors.lastName}>
            <FormLabel size="md">Last name</FormLabel>
            <Input
              placeholder="Your last name"
              type="text"
              value={formik.values.lastName}
              onChange={(event) => {
                formik.setFieldValue("lastName", event.target.value);
                if (formik.errors.lastName) {
                  formik.setFieldError("lastName", undefined);
                }
              }}
              size="lg"
            />
            {formik.errors.lastName ? (
              <FormHelperText>
                <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                {formik.errors.lastName}
              </FormHelperText>
            ) : null}
          </FormControl>
        </Stack>
        <Button
          size="lg"
          variant="primary"
          endDecorator={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
          type="submit"
          loading={loading}
        >
          Continue
        </Button>
      </Stack>
    </AuthCard>
  );
};

export { AuthInviteSetName };
